import { useEffect, useCallback, useRef, useState } from 'react';

const shouldLogPerf =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_ENABLE_PERF_LOGS === 'true';

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export const usePerformance = (componentName: string) => {
  const renderStartTime = now();
  const hasMarkedStart = useRef<boolean>(false);

  useEffect(() => {
    const renderTime = now() - renderStartTime;

    if (shouldLogPerf && renderTime > 120) {
      console.warn(`Slow render detected: ${componentName} took ${renderTime.toFixed(2)}ms`);
    }

    if (process.env.NODE_ENV === 'production' && hasMarkedStart.current) {
      try {
        performance.mark(`${componentName}-render-end`);
        performance.measure(`${componentName}-render`, `${componentName}-render-start`, `${componentName}-render-end`);
      } catch {
        // No-op: performance marks are best-effort only.
      }
    }
  });

  const markRenderStart = useCallback(() => {
    if (process.env.NODE_ENV === 'production') {
      try {
        performance.mark(`${componentName}-render-start`);
        hasMarkedStart.current = true;
      } catch {
        // No-op: performance marks are best-effort only.
      }
    }
  }, [componentName]);

  return { markRenderStart };
};

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useThrottle = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T => {
  const lastCall = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(
    ((...args: Parameters<T>) => {
      const currentTime = Date.now();

      if (currentTime - lastCall.current >= delay) {
        lastCall.current = currentTime;
        return callback(...args);
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        lastCall.current = Date.now();
        callback(...args);
      }, delay - (currentTime - lastCall.current));

      return undefined;
    }) as T,
    [callback, delay]
  );
};
